import React, { useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { FileRejection } from "react-dropzone";
import { blue, grey } from "@mui/material/colors";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ListAltIcon from "@mui/icons-material/ListAlt";

import { useAppDispatch } from "app/hooks";
import { downloadErrorFile } from "../../store/znsCampaignSlice";

type UploadStatus = "queued" | "uploading" | "success" | "failed" | "campaign";

export type UploadRecipientMode = "simple" | "menu";

type UploadItem = {
  id: string;
  file?: File;
  name?: string;
  status: UploadStatus;
  message?: string; // e.g. error message or server response
  errorFilePath?: string;
  recipientCount?: number;
};

const bytes = (mb: number) => mb * 1024 * 1024;

function formatBytes(n: number) {
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function rejectionMessage(r: FileRejection, maxSizeBytes: number) {
  const codes = new Set(r.errors.map((e) => e.code));
  if (codes.has("file-invalid-type")) return "Invalid File Format.";
  if (codes.has("file-too-large"))
    return `The file size exceeds the limit allowed (>${formatBytes(
      maxSizeBytes,
    )}).`;
  return r.errors.map((e) => e.message).join(" ") || "Invalid File.";
}

function statusColor(status: UploadStatus) {
  switch (status) {
    case "queued":
      return "text.secondary";
    case "uploading":
      return "primary.main";
    case "success":
      return "success.main";
    case "failed":
      return "error.main";
    case "campaign":
      return "primary.main";
  }
}

export const UploadRecipientSet = ({
  uploadFile = async (file: File) => {},
  maxSizeMB = 3,
  multiple = true,
  onReset,
  open = false,
  onClose,
  mode = "simple",
  onSelectFromCampaign,
  disabled = false,
  selectedCampaign,
}: {
  uploadFile: (file: File) => Promise<void>;
  maxSizeMB?: number;
  multiple?: boolean;
  onReset?: (resetFn: () => void) => void;
  open?: boolean;
  onClose?: () => void;
  mode?: UploadRecipientMode;
  onSelectFromCampaign?: () => void;
  disabled?: boolean;
  selectedCampaign?: any;
}) => {
  const dispatch = useAppDispatch();
  const [items, setItems] = React.useState<UploadItem[]>([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const resetItems = React.useCallback(() => {
    setItems([]);
  }, []);

  React.useEffect(() => {
    if (selectedCampaign) {
      setItems([
        {
          id: selectedCampaign.id,
          name: selectedCampaign.name,
          status: "campaign",
          recipientCount: selectedCampaign.totalUser,
        },
      ]);
    }
  }, [selectedCampaign]);

  React.useEffect(() => {
    if (onReset) {
      onReset(resetItems);
    }
  }, [onReset, resetItems]);

  const handleClose = () => {
    setModalOpen(false);
    if (onClose) onClose();
  };

  React.useEffect(() => {
    if (open) {
      setModalOpen(true);
    }
  }, [open]);

  const handleAddRecipientClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (mode === "menu") {
      setAnchorEl(event.currentTarget);
    } else {
      setModalOpen(true);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUploadRecipientList = () => {
    handleMenuClose();
    setModalOpen(true);
  };

  const handleSelectFromCampaignClick = () => {
    handleMenuClose();
    if (onSelectFromCampaign) onSelectFromCampaign();
  };

  const removeItem = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const maxSizeBytes = bytes(maxSizeMB);

  const startUpload = React.useCallback(
    async (id: string, file: File) => {
      setItems((prev) =>
        prev.map((x) =>
          x.id === id ? { ...x, status: "uploading", message: undefined } : x,
        ),
      );

      try {
        await uploadFile(file);
        setItems((prev) =>
          prev.map((x) => (x.id === id ? { ...x, status: "success" } : x)),
        );
      } catch (err: any) {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          "Upload failed. Please try again.";
        setItems((prev) =>
          prev.map((x) =>
            x.id === id
              ? {
                  ...x,
                  status: "failed",
                  message: msg,
                  errorFilePath: err.errorFilePath,
                }
              : x,
          ),
        );
      }
    },
    [uploadFile],
  );

  const onDropAccepted = useCallback(
    (files: File[]) => {
      handleClose();
      // add queue items
      const newItems: UploadItem[] = files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        status: "queued",
      }));

      setItems((prev) =>
        multiple ? [...newItems, ...prev] : [newItems[0], ...prev],
      );

      // auto upload immediately after adding
      for (const it of multiple ? newItems : [newItems[0]]) {
        if (it.file) {
          void startUpload(it.id, it.file);
        }
      }
    },
    [multiple, startUpload],
  );

  const onDropRejected = React.useCallback(
    (rejections: FileRejection[]) => {
      // Rejected depens on the UX "status list"
      const failedItems: UploadItem[] = rejections.map((r) => ({
        id: crypto.randomUUID(),
        file: r.file,
        status: "failed",
        message: rejectionMessage(r, maxSizeBytes),
      }));

      setItems((prev) =>
        multiple ? [...failedItems, ...prev] : [failedItems[0], ...prev],
      );
    },
    [maxSizeBytes, multiple],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      multiple,
      maxSize: maxSizeBytes,
      accept: {
        "text/csv": [".csv"],
        "application/vnd.ms-excel": [".csv"],
      },
      onDropAccepted,
      onDropRejected,
    });

  return (
    <Paper
      sx={{
        width: "100%",
        p: 2,
        gap: 2,
        display: "flex",
        flexDirection: "column",
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="body1" fontWeight="bold">
            Recipient Set-up
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddRecipientClick}
          endIcon={mode === "menu" ? <KeyboardArrowDownIcon /> : undefined}
          disabled={disabled || items.some((it) => it.status === "campaign")}
        >
          ADD RECIPIENT
        </Button>
        {mode === "menu" && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleUploadRecipientList}>
              Upload recipient list
            </MenuItem>
            <MenuItem onClick={handleSelectFromCampaignClick}>
              Select from campaign
            </MenuItem>
          </Menu>
        )}
      </Box>
      {items.length > 0 && (
        <List dense>
          {items.map((it) => (
            <ListItem
              key={it.id}
              sx={{
                alignItems: "center",
              }}
              secondaryAction={
                <Stack direction="row" spacing={1} alignItems="center">
                  {it.status === "uploading" && <CircularProgress size={18} />}

                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => removeItem(it.id)}
                    disabled={it.status === "uploading"}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {it.status === "campaign" ? (
                  <ListAltIcon sx={{ color: statusColor(it.status) }} />
                ) : (
                  <UploadFileIcon sx={{ color: statusColor(it.status) }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 500, color: statusColor(it.status) }}
                  >
                    {it.status === "campaign" ? it.name : it.file?.name}
                  </Typography>
                }
                secondary={
                  <Box>
                    {it.status === "campaign" ? (
                      <Typography variant="caption" color="text.secondary">
                        {it.recipientCount !== undefined
                          ? it.recipientCount + " Recipients"
                          : "unknown"}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: it.errorFilePath
                            ? "error.main"
                            : "text.secondary",
                        }}
                      >
                        {it.file ? formatBytes(it.file.size) : ""} {" • "}
                        {it.errorFilePath && (
                          <>
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{
                                color: "error.main",
                                cursor: "pointer",
                                textDecoration: "underline",
                                mt: 0.5,
                              }}
                              onClick={() => {
                                if (it.errorFilePath) {
                                  dispatch(downloadErrorFile(it.errorFilePath));
                                }
                              }}
                            >
                              Download detail error file
                            </Typography>
                            {" • "}
                          </>
                        )}
                        {it.status === "uploading"
                          ? "Uploading"
                          : it.status === "success"
                            ? "Done"
                            : it.status === "failed"
                              ? "Failed"
                              : "Queued"}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      <Dialog open={modalOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          Upload recipient list
          <Typography variant="body2" color="text.secondary">
            Please check the information before saving the automatic message!
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                borderRadius: "4px",
                backgroundColor: blue[100],
                width: "100%",
                p: 2,
              }}
            >
              <InfoOutlinedIcon sx={{ color: blue[500] }} />
              <Box>
                <Typography variant="body2">
                  Ensure the recipient list file is in the required format:
                  <ul style={{ margin: 0, padding: "0 0 0 16px" }}>
                    <li>File format: CSV</li>
                    <li>Maximum list length: 100,000 rows</li>
                    <li>Column names must match the parameter names.</li>
                  </ul>
                </Typography>
              </Box>
            </Stack>
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed",
                borderColor: isDragReject
                  ? "error.main"
                  : isDragActive
                    ? "primary.main"
                    : grey[400],
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                transition: "0.2s",
              }}
            >
              <input {...getInputProps()} />
              <UploadFileIcon sx={{ color: blue[600] }} />
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                {isDragActive
                  ? "Drop your file here..."
                  : "Click to upload or drag and drop"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CSV (max. {maxSizeMB}MB)
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};
